import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export type AntwoordState = "draft" | "pending" | "synced" | "rejected";

/** The waarnemer's current antwoord to one vraag for one ploeg; a later one replaces it. */
export interface LocalAntwoord {
  key: string;
  contactId: number;
  baanId: number;
  id: string;
  uitslagId: number;
  slot: string;
  value: string;
  opmerking: string | null;
  givenAt: string;
  state: AntwoordState;
  reason?: number;
  /** Who gave it namens the waarnemer, as `ownerOf` keys that person; unset when the waarnemer gave it (ADR-0019). */
  enteredBy?: string;
  /** Set only on the copy of a value VBS already held; never stored on the device. */
  fromVbs?: boolean;
}

/** The waarnemer's free text about one ploeg; a later one replaces it, and it is never pushed to VBS. */
export interface LocalNotitie {
  key: string;
  contactId: number;
  baanId: number;
  uitslagId: number;
  text: string;
  givenAt: string;
  state: Exclude<AntwoordState, "draft">;
  reason?: number;
  /** Who wrote it namens the waarnemer, as for a `LocalAntwoord` (ADR-0019). */
  enteredBy?: string;
}

interface VbsJuryDb extends DBSchema {
  antwoorden: { key: string; value: LocalAntwoord };
  notities: { key: string; value: LocalNotitie };
  cache: { key: string; value: unknown };
}

export type LocalDb = IDBPDatabase<VbsJuryDb>;

export function openLocalDb(): Promise<LocalDb> {
  return openDB<VbsJuryDb>("vbs-jury", 2, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore("antwoorden", { keyPath: "key" });
        db.createObjectStore("cache");
      }
      if (oldVersion < 2) db.createObjectStore("notities", { keyPath: "key" });
    },
  });
}

let shared: Promise<LocalDb> | undefined;

export function localDb(): Promise<LocalDb> {
  shared ??= openLocalDb();
  return shared;
}

/** Tells open screens that local data changed, so they read it again. */
export const localChanges = new EventTarget();

export function notifyLocalChange() {
  localChanges.dispatchEvent(new Event("change"));
}
